// Settings › Admin configuration console (lane B10 S20a; plan D14; offer.md calls 21–22; DC-215 "The TA Admin
// configuration console is a settings rail plus a versioned table."; CR-066 "Change -> Validate -> Version -> Activate ->
// Audit"). `#settings/config/<area>[/<name>]`: the rail of the registered areas (./areas.ts — S20a's five list areas and
// S20b's Thresholds, Reminders and Templates) with *Draft* under an area holding a version that waits on its second TA
// Admin, the link to B4's level framework page (`#settings/framework/<level>`), tabs for the keys of the area, and the
// area's Editor for the key in hand. `#settings` lands here for the Admin; another reader the route admits (one holding
// `permissions.matrix` without `config.list`) goes on to S20b's permission matrix. The console is the Admin's: its
// gate is `config.list` (the matrix row *Business configuration*); a route naming no area or no key is not-found.

import { McPage } from "@/shell/page";
import { useEffect, useMemo, type ReactNode } from "react";
import type { ProposalRow } from "@/api/tools";
import { useRead } from "@/api/useTool";
import type { Route } from "@/router";
import { useSession } from "@/session/useSession";
import { useViewState } from "@/shell/viewState";
import { V1Slot } from "@/shell/island";
import { NotFound } from "@/views/NotFound";
import {
  EmptyState, Flex, PANEL_CHANGE_STATUS, PageHeader, SectionRail, Skeleton, Stack, Tabs, type SectionRailItem,
} from "@/mc";
import {
  areaOf, consoleArea, consoleAreas, consoleHref, keyFromRoute, keysOf, kindOf, nameOf, parseConsoleRoute, type ConsoleArea,
} from "./areas";
import { listVersions } from "./AreaTable";
import { Refusal } from "./Journey";
import { CONSOLE_LABELS, keyWord } from "./labels";
import { hsKey } from "@/views/b10/lib/hsKey";

/** B4's level framework page (B4 S11, `#settings/framework/<level>`; plan §0.3 N3), linked from the rail at its first
 * level. */
export const FRAMEWORK_HREF = "#settings/framework/manager_and_below";

/** Where `#settings` sends a reader who may not open the console: S20b's permission matrix. */
export const NOT_CONSOLE_HREF = "#settings/permissions";

/** `#settings`: the console for the Admin; the permission matrix for another reader the route admits. */
export function SettingsLanding({ route }: { route: Route }) {
  const { can } = useSession();
  const opens = can("config.list");
  useEffect(() => {
    if (!opens) window.location.replace(NOT_CONSOLE_HREF);
  }, [opens]);
  return opens ? <Console route={route} /> : null;
}

/** `#settings/config/<area>[/<name>]`. */
export function Console({ route }: { route: Route }) {
  const { register } = useViewState();
  const { area: areaKey, name } = parseConsoleRoute(route.parts);
  const areas = consoleAreas();
  const area = areaKey === undefined ? areas[0] : consoleArea(areaKey);
  const all = useRead(["config", "all"], () => listVersions());
  const versions = useMemo(() => all.data ?? [], [all.data]);
  const known = useMemo(() => [...new Set(versions.map((row) => row.key))], [versions]);
  const keys = area ? keysOf(area, known, areas) : [];
  const configKey = name === undefined ? keys[0] : keyFromRoute(name, keys);
  useEffect(
    () => register({ view: "settings", ids: {}, summary: { tab: area?.key ?? "", loading: all.isPending } }),
    [register, area?.key, all.isPending],
  );
  if (!area || (name !== undefined && all.data !== undefined && configKey === undefined)) return <V1Slot><NotFound route={route.raw} /></V1Slot>;
  return (
    <McPage>
      <ConsolePage
        area={area}
        areas={areas}
        keys={keys}
        configKey={configKey}
        versions={versions}
        loading={all.isPending}
        error={<Refusal error={all.error} onRetry={() => void all.refetch()} />}
      />
    </McPage>
  );
}

interface PageProps {
  area: ConsoleArea;
  areas: readonly ConsoleArea[];
  keys: readonly string[];
  configKey: string | undefined;
  versions: readonly ProposalRow[];
  loading: boolean;
  error: ReactNode;
}

function ConsolePage({ area, areas, keys, configKey, versions, loading, error }: PageProps) {
  // an area holding a version that waits on its second Admin shows *Draft* under its name
  const waiting = new Set(versions.filter((row) => row.status === "draft").map((row) => areaOf(row.key, areas)?.key));
  const rail: SectionRailItem[] = [
    ...areas.map((a): SectionRailItem => ({
      key: a.key,
      title: a.label,
      href: consoleHref(a.key),
      state: a.key === area.key ? "on" : "todo",
      status: waiting.has(a.key) ? PANEL_CHANGE_STATUS.draft : undefined,
      hs: hsKey("cfg.area", a.key),
    })),
    { key: "framework", title: CONSOLE_LABELS.taFramework, href: FRAMEWORK_HREF, hs: "cfg.framework" },
  ];
  const Editor = area.Editor;
  return (
    <Stack gap={20}>
      <PageHeader title={CONSOLE_LABELS.console} meta={[area.label]} />
      <Flex align="start" gap={24}>
        <SectionRail label={CONSOLE_LABELS.console} items={rail} />
        <Stack gap={16} fill>
          {error}
          {keys.length > 1 ? (
            <Tabs
              route
              label={area.label}
              active={configKey}
              items={keys.map((key) => ({
                key,
                label: keyWord(key) ?? nameOf(key),
                href: consoleHref(area.key, key),
                hs: hsKey("cfg.key", nameOf(key)),
              }))}
            />
          ) : null}
          {configKey !== undefined ? (
            <Editor key={configKey} kind={kindOf(configKey)} configKey={configKey} />
          ) : loading ? (
            <Skeleton lines={6} />
          ) : (
            <EmptyState>{area.label}</EmptyState>
          )}
        </Stack>
      </Flex>
    </Stack>
  );
}
